import React, { useEffect, useMemo, useRef, useState } from 'react'
import { VehicleModel } from '@/app/models'
import { FaIcon, faTruckSolid } from '@/lib/fa'
import { faClose, faSearch } from '@fortawesome/pro-light-svg-icons'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { axiosInstance } from '@/modules/shared/lib/axios'

type VehiclePickerProps = {
  companyId: string
  onSubmit: (vehicleId: string) => void
  close: () => void
}

export function VehiclePicker({
  companyId,
  onSubmit,
  close,
}: VehiclePickerProps) {
  const { t } = useLocalize()
  const { groups, setActualGroup, actualGroup } = useVehicles()

  const [vehicles, setVehicles] = useState<VehicleModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const defaultGroups = [
    { id: -1, name: t('vehicleModule.active-vehicles') },
    { id: 0, name: t('vehicleModule.all-label') },
    { id: -2, name: t('vehicleModule.inactive-vehicles') },
  ]

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const res = await axiosInstance.get(`/api/${companyId}/vehicle/list`)
        setVehicles(res.data ?? [])
      } catch {
        setVehicles([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [companyId])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [close])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [close])

  const filtered = useMemo(() => {
    if (!query.trim()) return vehicles
    const q = query.toLowerCase()
    return vehicles.filter(
      (v) =>
        v.registrationNumber?.toLowerCase().includes(q) ||
        v.make?.toLowerCase().includes(q) ||
        v.model?.toLowerCase().includes(q),
    )
  }, [vehicles, query])

  const isTyped = query.trim().length > 0
  const customGroups = useMemo(() => groups.filter((g) => g.id > 0), [groups])

  return (
    <div
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      className="tw-absolute tw-left-0 tw-top-full tw-z-[999] tw-mt-1 tw-flex tw-max-h-[85vh] tw-w-[300px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-shadow-xl"
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-3 tw-py-2">
        <div className="tw-flex tw-flex-col">
          <span className="tw-font-semibold tw-text-content-base">
            {t('newDelegation.choose-vehicle')}
          </span>
        </div>
        <button
          type="button"
          onClick={close}
          className="tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded hover:tw-bg-base-300"
        >
          <FaIcon icon={faClose} className="tw-h-3 tw-w-3" />
        </button>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-2">
        <div className="tw-relative">
          <FaIcon
            icon={faSearch}
            className="tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-content-base/50"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('vehicleModule.search-vehicle')}
            className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-py-1.5 tw-pl-8 tw-pr-3 tw-text-sm tw-text-content-base tw-outline-none focus:tw-border-primary"
          />
        </div>
      </div>

      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <div className="tw-h-[200px] tw-w-full tw-overflow-y-auto tw-border-t tw-border-base-300">
          {isLoading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
              <span className="tw-text-sm tw-text-content-base/60">
                {t('common.loading')}
              </span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
              <span className="tw-text-sm tw-text-content-base/60">
                {t('common.no-data')}
              </span>
            </div>
          ) : (
            filtered.map((vehicle) => (
              <button
                key={vehicle.id}
                type="button"
                onClick={() => onSubmit(String(vehicle.id))}
                className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors last:tw-border-b-0 hover:tw-bg-primary/5"
              >
                <div className="tw-flex tw-flex-col tw-overflow-hidden">
                  <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content-base">
                    {vehicle.registrationNumber}
                  </span>
                  {vehicle.make && (
                    <span className="tw-truncate tw-text-xs tw-text-content-base/60">
                      {vehicle.make} {vehicle.model}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {!isTyped && (
          <div className="tw-flex tw-w-full tw-flex-col tw-border-t tw-border-base-300">
            <div className="tw-px-3 tw-pb-1 tw-pt-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base tw-opacity-80">
                {t('driverModule.groups')}
              </span>
            </div>
            <div className="tw-flex tw-max-h-[200px] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-px-2 tw-pb-2">
              {defaultGroups.map((group) => {
                const isActive = actualGroup?.id === group.id
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => {
                      setActualGroup(group)
                      close()
                    }}
                    className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-px-1 tw-py-2 ${
                      isActive ? 'tw-bg-secondary' : 'hover:tw-bg-base-300/40'
                    }`}
                  >
                    <FaIcon icon={faTruckSolid} />
                    <span
                      className={`tw-text-content-base ${isActive ? 'tw-font-medium' : 'tw-text-base'}`}
                    >
                      {group.name}
                    </span>
                  </button>
                )
              })}
              {customGroups.length > 0 && (
                <>
                  <div className="tw-my-1 tw-border-t tw-border-base-300" />
                  {customGroups.map((group) => {
                    const isActive = actualGroup?.id === group.id
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => {
                          setActualGroup(group)
                          close()
                        }}
                        className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-px-1 tw-py-2 ${
                          isActive
                            ? 'tw-bg-secondary'
                            : 'hover:tw-bg-base-300/40'
                        }`}
                      >
                        <FaIcon icon={faTruckSolid} />
                        <span
                          className={`tw-text-content-base ${isActive ? 'tw-font-medium' : 'tw-text-base'}`}
                        >
                          {group.name}
                        </span>
                      </button>
                    )
                  })}
                </>
              )}
            </div>
          </div>
        )}

        {/* 
        {isTyped ? (
          <div className="tw-h-fit tw-w-full tw-border-t tw-border-base-300">
            {isLoading ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                <span className="tw-text-sm tw-text-content-base/60">
                  {t('common.loading')}
                </span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                <span className="tw-text-sm tw-text-content-base/60">
                  {t('common.no-data')}
                </span>
              </div>
            ) : (
              filtered.map((vehicle) => (
                <button
                  key={vehicle.id}
                  type="button"
                  onClick={() => onSubmit(String(vehicle.id))}
                  className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors last:tw-border-b-0 hover:tw-bg-primary/5"
                >
                  <div className="tw-flex tw-flex-col tw-overflow-hidden">
                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content-base">
                      {vehicle.registrationNumber}
                    </span>
                    {vehicle.make && (
                      <span className="tw-truncate tw-text-xs tw-text-content-base/60">
                        {vehicle.make} {vehicle.model}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="tw-flex tw-w-full tw-flex-col">
            <div className="tw-px-3 tw-pb-1 tw-pt-1">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base tw-opacity-80">
                {t('driverModule.groups')}
              </span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1 tw-overflow-y-auto tw-px-2">
              {defaultGroups.map((group) => {
                const isActive = actualGroup?.id === group.id
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => {
                      setActualGroup(group)
                      close()
                    }}
                    className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-px-1 tw-py-2 ${
                      isActive ? 'tw-bg-secondary' : 'hover:tw-bg-base-300/40'
                    }`}
                  >
                    <FaIcon icon={faTruckSolid} />
                    <span
                      className={`tw-text-content-base ${isActive ? 'tw-font-medium' : 'tw-text-base'}`}
                    >
                      {group.name}
                    </span>
                  </button>
                )
              })}
              {customGroups.length > 0 && (
                <>
                  <div className="tw-my-1 tw-border-t tw-border-base-300" />
                  {customGroups.map((group) => {
                    const isActive = actualGroup?.id === group.id
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => {
                          setActualGroup(group)
                          close()
                        }}
                        className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-px-1 tw-py-2 ${
                          isActive
                            ? 'tw-bg-secondary'
                            : 'hover:tw-bg-base-300/40'
                        }`}
                      >
                        <FaIcon icon={faTruckSolid} />
                        <span
                          className={`tw-text-content-base ${isActive ? 'tw-font-medium' : 'tw-text-base'}`}
                        >
                          {group.name}
                        </span>
                      </button>
                    )
                  })}
                </>
              )}
            </div>
          </div>
        )}
        */}
      </div>
    </div>
  )
}

export default VehiclePicker
